import { TrendingUp, MessageSquare } from 'lucide-react'
import type { ForeignWord, Alternative } from '../../../types/word-discovery'

interface WordCardProps {
  word: ForeignWord
  onClick: () => void
}

export function WordCard({ word, onClick }: WordCardProps) {
  const ariaLabel = `عرض تفاصيل كلمة ${word.wordArabic} - ${word.word}. البديل الأشهر: ${word.topAlternative?.text || 'لا توجد بدائل'}. عدد البدائل: ${word.alternativesCount}، مجموع الأصوات: ${word.totalVotes}`

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="group relative w-full bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-amber-500/30 rounded-xl p-6 text-right transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/5"
    >
      <CornerAccent />
      <ForeignWordDisplay word={word.word} wordArabic={word.wordArabic} />
      <div className="mb-4">
        <TopAlternativeDisplay alternative={word.topAlternative} />
      </div>
      <div className="flex items-center justify-between">
        <HoverIndicator />
        <WordStats alternativesCount={word.alternativesCount} totalVotes={word.totalVotes} />
      </div>
    </button>
  )
}

function CornerAccent() {
  return (
    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-500/10 to-transparent rounded-br-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
  )
}

interface ForeignWordDisplayProps {
  word: string
  wordArabic: string
}

function ForeignWordDisplay({ word, wordArabic }: ForeignWordDisplayProps) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <div className="text-lg font-medium text-slate-300">{word}</div>
      <div className="text-lg font-semibold text-slate-100">
        {wordArabic}
      </div>
    </div>
  )
}

interface TopAlternativeDisplayProps {
  alternative: Alternative | undefined
}

function TopAlternativeDisplay({ alternative }: TopAlternativeDisplayProps) {
  return (
    <div className="flex-1 bg-amber-500/10 rounded-lg p-3 -mx-1">
      <div className="text-sm text-slate-400 mb-2">البديل الأشهر</div>
      <div className="text-3xl font-bold text-amber-400 tracking-wide">
        {alternative?.text || 'لا توجد بدائل حالياً'}
      </div>
    </div>
  )
}

interface WordStatsProps {
  alternativesCount: number
  totalVotes: number
}

function WordStats({ alternativesCount, totalVotes }: WordStatsProps) {
  return (
    <div className="flex items-center justify-end gap-4 text-sm">
      <div className="flex items-center gap-1.5 text-rose-400">
        <MessageSquare className="w-4 h-4" />
        <span>{alternativesCount}</span>
      </div>
      <div className="flex items-center gap-1.5 text-amber-400">
        <TrendingUp className="w-4 h-4" />
        <span>{totalVotes}</span>
      </div>
    </div>
  )
}

function HoverIndicator() {
  return (
    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <div className="text-xs text-amber-500 font-medium">اضغط للتفاصيل ←</div>
    </div>
  )
}
